// components/agent/AvailableLeads.js
import React, { useState, useEffect, useRef } from 'react';
import { HelpCircle, Users, ChevronLeft, ChevronRight, MapPin } from 'react-feather';
import AuthService from '../../services/authService';
import LeadClaimModal from './LeadClaimModal';
import PointsPurchaseModal from './PointsPurchaseModal';
import SupportChat from '../common/SupportChat';
import AvailableLeadModal from './AvailableLeadModal';
import ActiveTerritoriesModal from './ActiveTerritoriesModal';
import darkIcon from '../../assets/dark-iconp.png';
import '../admin/AdminDashboard.css';
import './AgentDashboard.css';

// US state code <-> name lookup, used to parse the freeform `lead.location`
// string into a normalized 2-letter state code for the demo-mode filter.
const US_STATES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia'
};
const STATE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(US_STATES).map(([code, name]) => [name.toLowerCase(), code])
);
const STATE_CODE_SET = new Set(Object.keys(US_STATES));

/**
 * Extract a 2-letter US state code from a freeform location string.
 * Handles: "Austin, TX", "Austin, Texas", "Texas", "TX",
 * "123 Main St, Austin, TX 78701", and similar. Returns null if no match.
 */
const extractStateCode = (loc) => {
  if (!loc || typeof loc !== 'string') return null;
  const trimmed = loc.trim();
  if (!trimmed || trimmed === 'N/A' || trimmed === 'Default Location') return null;

  // 1. Try comma-segmented parts from right to left — most reliable for
  //    address-like strings ("123 Main St, Austin, TX 78701").
  const parts = trimmed.split(',').map(p => p.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    // Strip trailing ZIP code: "TX 78701" -> "TX"
    const noZip = part.replace(/\s+\d{5}(-\d{4})?$/, '').trim();
    const upper = noZip.toUpperCase();
    if (STATE_CODE_SET.has(upper)) return upper;
    const fromName = STATE_NAME_TO_CODE[noZip.toLowerCase()];
    if (fromName) return fromName;
  }

  // 2. No commas (or none matched) — try the whole string as code or name.
  const noZip = trimmed.replace(/\s+\d{5}(-\d{4})?$/, '').trim();
  const upper = noZip.toUpperCase();
  if (STATE_CODE_SET.has(upper)) return upper;
  const fromName = STATE_NAME_TO_CODE[noZip.toLowerCase()];
  if (fromName) return fromName;

  // 3. Scan word-by-word for a state code or name as a last resort.
  //    Handles edge cases like "Austin TX" (no comma).
  const words = noZip.split(/\s+/);
  for (let i = words.length - 1; i >= 0; i--) {
    const w = words[i].toUpperCase();
    if (STATE_CODE_SET.has(w)) return w;
  }
  // Two-word state names (e.g., "New York", "South Carolina")
  for (let i = 0; i < words.length - 1; i++) {
    const pair = `${words[i]} ${words[i + 1]}`.toLowerCase();
    if (STATE_NAME_TO_CODE[pair]) return STATE_NAME_TO_CODE[pair];
  }

  return null;
};

const AvailableLeads = ({ agentInfo }) => {
  // Demo accounts get a different view: full pool with claimed badges,
  // and a state-level filter instead of a city/territory filter.
  // The `agentInfo` prop may be the full agent object OR just the nested
  // agentInfo subdoc, depending on what the parent passes — handle both.
  const isDemoAccount =
    agentInfo?.isDemoAccount === true ||
    agentInfo?.agentInfo?.isDemoAccount === true;

  const [availableLeads, setAvailableLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentPoints, setAgentPoints] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('available');
  
  // Filter state
  const [tierFilter, setTierFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [availableLocations, setAvailableLocations] = useState([]);
  // Demo-only: state-level filter (replaces locationFilter when isDemoAccount).
  // Defaults to CO for demos so we never accidentally fetch every user.
  const [stateFilter, setStateFilter] = useState(isDemoAccount ? 'CO' : 'all');
  const [availableStates, setAvailableStates] = useState(
    // Pre-seed the dropdown with a curated list so the demo always has
    // states to flip between, even before any fetch has completed.
    isDemoAccount ? ['CO', 'TX', 'UT'] : []
  );
  // Per-state cache so flipping back to a previously-loaded state/page is instant.
  // Shape: { [stateCode]: { [pageNum]: leads[], total: number } }
  const demoStateCache = useRef({});
  // Server-side total count for demo accounts (set after each demo fetch).
  const [demoTotalCount, setDemoTotalCount] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 10;
  const tableScrollRef = useRef(null);
  
  // Modal state
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  // Leads this agent explicitly passed on this session. Merged with the
  // server's lead.hasDeclined at render time so the badge appears without a refetch.
  const [declinedLeadIds, setDeclinedLeadIds] = useState(() => new Set());
  const [showTerritoriesModal, setShowTerritoriesModal] = useState(false);

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    // For demo accounts, the stateFilter/currentPage effect below drives
    // fetching so we don't double-fetch on mount. Non-demo agents fetch
    // normally here.
    if (!isDemoAccount) {
      fetchAvailableLeads();
    }
    fetchAgentPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Demo accounts: re-fetch when state, tier, or page changes. Cached pages
  // are served instantly; uncached pages trigger a single bounded fetch.
  useEffect(() => {
    if (!isDemoAccount) return;
    fetchAvailableLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, tierFilter, currentPage]);

  // Reset to page 1 when filters or tab change.
  // Skipped for `currentPage` itself — that would create an infinite loop
  // for demo accounts (page change triggers fetch which would reset page).
  useEffect(() => {
    setCurrentPage(1);
  }, [tierFilter, locationFilter, stateFilter, activeTab]);

  const fetchAgentPoints = async () => {
    try {
      const response = await fetch(`${API_URL}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAgentPoints(data.data.pointsBalance || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching agent points:', error);
    }
  };

  const fetchAvailableLeads = async () => {
    try {
      // ============================================
      // DEMO ACCOUNTS: paginated per-state fetch w/ cache
      // ============================================
      if (isDemoAccount) {
        // 'all' isn't a valid backend value — fall back to CO.
        const targetState = (!stateFilter || stateFilter === 'all') ? 'CO' : stateFilter;
        const page = currentPage;
        const pageSize = leadsPerPage;
        // Tier filter is server-side for demo so it works across pages.
        const tier = tierFilter && tierFilter !== 'all' ? tierFilter : null;
        // Cache key includes tier — different tier = different result set.
        const cacheKey = `${targetState}|${tier || 'all'}`;

        // Serve from cache if we've already loaded this state+tier+page.
        const bucket = demoStateCache.current[cacheKey];
        if (bucket && bucket[page]) {
          setAvailableLeads(bucket[page]);
          if (typeof bucket.total === 'number') {
            setDemoTotalCount(bucket.total);
          }
          setIsLoading(false);
          setError('');
          return;
        }

        setIsLoading(true);
        setError('');

        const params = new URLSearchParams({
          scope: 'state',
          state: targetState,
          page: String(page),
          pageSize: String(pageSize)
        });
        if (tier) params.set('tier', tier);
        const url = `${API_URL}/agent-pools/available-leads?${params.toString()}`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch available leads');
        }

        const data = await response.json();
        const leads = (data.success && data.data) ? data.data : [];
        const total = typeof data.total === 'number' ? data.total : leads.length;

        // Cache page + total under the (state|tier) bucket.
        if (!demoStateCache.current[cacheKey]) {
          demoStateCache.current[cacheKey] = {};
        }
        demoStateCache.current[cacheKey][page] = leads;
        demoStateCache.current[cacheKey].total = total;

        setAvailableLeads(leads);
        setDemoTotalCount(total);
        return;
      }

      // ============================================
      // NON-DEMO AGENTS: original behavior
      // ============================================
      setIsLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/agent-pools/available-leads?limit=300`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available leads');
      }

      const data = await response.json();

      if (data.success && data.data) {
        setAvailableLeads(data.data);

        // Extract unique locations for the territory dropdown.
        const locations = [...new Set(data.data
          .map(lead => lead.location)
          .filter(loc => loc && loc !== 'N/A')
        )].sort();
        setAvailableLocations(locations);
      } else {
        setAvailableLeads([]);
      }
    } catch (error) {
      console.error('Error fetching available leads:', error);
      setError('Failed to load available users. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClaimClick = (leadId) => {
    // Demo accounts can OPEN the claim modal (to see lead details), but
    // the modal itself disables the actual Claim button.
    setSelectedLeadId(leadId);
    setShowClaimModal(true);
  };

  const handleClaimSuccess = () => {
    setShowClaimModal(false);
    setSelectedLeadId(null);
    // Refresh the lists
    fetchAvailableLeads();
    fetchAgentPoints();
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // Explicit pass from the detail modal. Badges the lead locally straight away
  // and closes the modal; if the write fails we log loudly, because a silent
  // failure looks identical to success until the page is reloaded.
  const handleNotForMe = async (leadId) => {
    if (!leadId) return;

    setDeclinedLeadIds(prev => {
      if (prev.has(leadId)) return prev;
      const next = new Set(prev);
      next.add(leadId);
      return next;
    });
    setShowLeadModal(false);
    setSelectedLead(null);

    try {
      const res = await fetch(`${API_URL}/agent-pools/leads/${leadId}/declined`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) {
        console.error(`Failed to persist lead decline (${res.status}):`, await res.text());
      }
    } catch (error) {
      console.error('Error marking lead declined:', error);
    }
  };

  const formatDisplayName = (name) => {
    if (!name) return 'Anonymous';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  };

  const getTierEmoji = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'platinum': return '💎';
      case 'gold': return '🥇';
      case 'silver': return '🥈';
      case 'bronze': return '🥉';
      case 'flex': return '⚪';
      default: return '❓';
    }
  };

  const getTierColor = (tier) => {
    switch(tier?.toLowerCase()) {
      case 'platinum': return '#60a5fa';
      case 'gold': return '#fbbf24';
      case 'silver': return '#94a3b8';
      case 'bronze': return '#fb923c';
      case 'flex': return '#d1d5db';
      default: return '#6b7280';
    }
  };

  // isStanding = lead has gone unclaimed for 90+ days (discounted pricing)
  // Kept for the claim flow / future cost display — the table no longer shows it.
  // eslint-disable-next-line no-unused-vars
  const getPointsCost = (tier, isStanding = false) => {
    if (isStanding) {
      switch(tier?.toLowerCase()) {
        case 'platinum': return 30;
        case 'gold': return 20;
        case 'silver': return 12;
        case 'bronze': return 7;
        case 'flex': return 7;
        default: return 0;
      }
    }
    switch(tier?.toLowerCase()) {
      case 'platinum': return 65;
      case 'gold': return 45;
      case 'silver': return 25;
      case 'bronze': return 10;
      case 'flex': return 10;
      default: return 0;
    }
  };

  // Rent vs Buy on the lead's most recent swipe.
  // Prefers a denormalized field on the user; falls back to the newest populated
  // like/dislike, which is only an approximation of swipe order.
  const getLastSwipeType = (lead) => {
    const raw =
      lead.lastSwipedListingType ||
      lead.lastSwipedProperty?.listingType ||
      lead.likes?.[lead.likes.length - 1]?.listingType ||
      lead.dislikes?.[lead.dislikes.length - 1]?.listingType;

    if (!raw) return null;
    const v = String(raw).toLowerCase();
    return (v.includes('rent') || v.includes('lease')) ? 'Rental' : 'Purchase';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const calculateMonthlyIncome = (income, frequency) => {
    if (!income) return null;
    
    switch(frequency?.toLowerCase()) {
      case 'weekly':
        return income * 4.33; // Average weeks per month
      case 'bi-weekly':
      case 'biweekly':
      case 'bi weekly':
        return income * 2.17; // Average bi-weekly periods per month
      case 'monthly':
      default:
        return income;
    }
  };

  // Get filtered leads based on active tab
  const getFilteredLeads = () => {
    const now = new Date();
    const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
    
    return availableLeads.filter(lead => {
      // For demo accounts the server already applied tier + state filters
      // for this page. Don't re-filter client-side (would create empty pages
      // when the active page has fewer leads in a tier than the page size).
      const tierMatch = isDemoAccount
        ? true
        : (tierFilter === 'all' || lead.leadTier?.toLowerCase() === tierFilter);

      // Demo accounts: backend already filtered by state (one state's worth
      // of data is what we get back). No client-side location filter needed.
      // Non-demo agents: filter by the exact city/territory location string.
      const locationMatch = isDemoAccount
        ? true
        : (locationFilter === 'all' || lead.location === locationFilter);

      const createdDate = new Date(lead.createdAt);
      const isOlderThan90 = createdDate <= ninetyDaysAgo;
      const tabMatch = activeTab === 'available' ? !isOlderThan90 : isOlderThan90;
      
      return tierMatch && locationMatch && tabMatch;
    });
  };

  const filteredLeads = getFilteredLeads();
  
  // Pagination calculations
  // Demo accounts paginate server-side: filteredLeads IS the current page,
  // and the total comes from the server. Non-demo agents paginate client-side.
  const totalPages = isDemoAccount
    ? Math.max(1, Math.ceil(demoTotalCount / leadsPerPage))
    : Math.ceil(filteredLeads.length / leadsPerPage);
  const startIndex = (currentPage - 1) * leadsPerPage;
  const endIndex = startIndex + leadsPerPage;
  const currentLeads = isDemoAccount
    ? filteredLeads
    : filteredLeads.slice(startIndex, endIndex);
  
  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll the table back to top
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const goToPrevious = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };
  
  const goToNext = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const selectStyle = {
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
    color: '#111',
    fontSize: '0.875rem',
    cursor: 'pointer',
    minWidth: '150px',
    outline: 'none',
    transition: 'border-color 0.12s'
  };
  
  const paginationButtonStyle = (isActive, isDisabled) => ({
    padding: '6px 12px',
    minWidth: '36px',
    backgroundColor: isActive ? '#111' : '#ffffff',
    border: isActive ? '1px solid #111' : '1px solid #e5e7eb',
    borderRadius: '8px',
    color: isActive ? '#ffffff' : (isDisabled ? '#d1d5db' : '#374151'),
    fontSize: '0.82rem',
    fontWeight: isActive ? '600' : '500',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled && !isActive ? 0.5 : 1,
    transition: 'all 0.12s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  });

  return (
    <>
      <style>{`
        @keyframes availableLeadsShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .al-skeleton {
          background: linear-gradient(90deg, 
            rgba(0, 0, 0, 0.04) 25%, 
            rgba(0, 0, 0, 0.08) 50%, 
            rgba(0, 0, 0, 0.04) 75%
          );
          background-size: 200% 100%;
          animation: availableLeadsShimmer 1.5s infinite;
          border-radius: 6px;
        }
        .light-mode .al-skeleton,
        .dashboard-container.light-mode .al-skeleton {
          background-image: linear-gradient(90deg, 
            rgba(0, 0, 0, 0.06) 25%, 
            rgba(0, 0, 0, 0.1) 50%, 
            rgba(0, 0, 0, 0.06) 75%
          ) !important;
          background-color: transparent !important;
          background-size: 200% 100% !important;
          animation: availableLeadsShimmer 1.5s infinite !important;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '24px', gap: '12px' }}>
        <button 
          className="add-property-btn"
          style={{
            backgroundColor: '#ffffff',
            color: '#111',
            border: '1px solid #e5e7eb',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            minWidth: '120px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.12s',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
          }}
          onClick={() => setShowPointsModal(true)}
        >
          <div style={{ fontSize: '15px', fontWeight: '700' }}>
            {agentPoints} Points
          </div>
          <div style={{ fontSize: '10px', opacity: '0.7', fontWeight: '400' }}>
            Add Points
          </div>
        </button>
        <button 
          className="add-property-btn"
          style={{
            backgroundColor: '#ffffff',
            color: '#111',
            border: '1px solid #e5e7eb',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.12s',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
          }}
          onClick={() => setShowSupportChat(true)}
        >
          <HelpCircle size={18} />
          Get Support
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '9px',
          padding: '11px 14px',
          marginBottom: '20px',
          color: '#b91c1c',
          fontSize: '0.875rem',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Tier:
          </label>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Tiers</option>
            <option value="platinum">💎 Platinum</option>
            <option value="gold">🥇 Gold</option>
            <option value="silver">🥈 Silver</option>
            <option value="bronze">🥉 Bronze</option>
            <option value="flex">⚪ Flex</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280',
            fontWeight: '500'
          }}>
            {isDemoAccount ? 'State:' : 'Location:'}
          </label>
          {isDemoAccount ? (
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={selectStyle}
            >
              {availableStates.map(st => (
                <option key={st} value={st}>
                  {US_STATES[st] ? `${US_STATES[st]} (${st})` : st}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All Locations</option>
              {availableLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          )}
        </div>

        <button
          onClick={() => setShowTerritoriesModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '9px',
            color: '#111',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.12s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <MapPin size={16} />
          Active Territories
        </button>

        {(tierFilter !== 'all' || (!isDemoAccount && locationFilter !== 'all')) && (
          <button
            onClick={() => {
              setTierFilter('all');
              if (!isDemoAccount) {
                setLocationFilter('all');
              }
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '6px',
              color: '#6b7280',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            Clear Filters
          </button>
        )}

        <div style={{ 
          marginLeft: 'auto', 
          fontSize: '0.875rem', 
          color: '#9ca3af' 
        }}>
          Showing {startIndex + 1}-{Math.min(endIndex, isDemoAccount ? demoTotalCount : filteredLeads.length)} of {isDemoAccount ? demoTotalCount : filteredLeads.length} users
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}>
        {/* Tab Headers */}
        <div style={{
          display: 'flex',
          gap: '16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => setActiveTab('available')}
            style={{
              padding: '9px 18px',
              backgroundColor: 'transparent',
              color: activeTab === 'available' ? '#111' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'available' ? '2px solid #111' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              transition: 'all 0.15s',
              marginBottom: '-1px',
              letterSpacing: '-0.005em'
            }}
          >
            Available Users
          </button>
          <button
            onClick={() => setActiveTab('standing')}
            style={{
              padding: '9px 18px',
              backgroundColor: 'transparent',
              color: activeTab === 'standing' ? '#111' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'standing' ? '2px solid #111' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              transition: 'all 0.15s',
              marginBottom: '-1px',
              letterSpacing: '-0.005em'
            }}
          >
            Standing Users (90+ Days Unclaimed)
          </button>
        </div>

        {isLoading ? (
          <div>
            {/* Skeleton table header */}
            <div style={{ 
              display: 'flex', 
              padding: '16px', 
              borderBottom: '2px solid rgba(0, 0, 0, 0.06)',
              gap: '16px'
            }}>
              {['100px', '70px', '80px', '90px', '85px', '100px', '80px', '60px'].map((w, i) => (
                <div key={i} className="al-skeleton" style={{ width: w, height: '14px' }} />
              ))}
            </div>
            {/* Skeleton rows */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '20px 16px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                gap: '16px'
              }}>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '110px', height: '16px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '80px', height: '14px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '48px', height: '32px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '100px', height: '14px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '50px', height: '14px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '70px', height: '14px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '48px', height: '32px' }} /></div>
                <div style={{ flex: 1 }}><div className="al-skeleton" style={{ width: '80px', height: '36px', borderRadius: '8px' }} /></div>
              </div>
            ))}
          </div>
        ) : filteredLeads.length > 0 ? (
          <>
            <div 
              ref={tableScrollRef}
              style={{ 
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: '520px',
              border: '1px solid #f0f0f0',
              borderRadius: '10px'
            }}>
              <table className="properties-table" style={{ marginBottom: 0 }}>
                <thead style={{ 
                  position: 'sticky', 
                  top: 0, 
                  backgroundColor: '#fafafa',
                  zIndex: 1
                }}>
                  <tr>
                    <th>Name</th>
                    <th>Tier</th>
                    <th>Last Swiped On</th>
                    <th>Location</th>
                    <th>Credit Score</th>
                    <th>Monthly Income</th>
                    <th>Swipes Made</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLeads.map((lead) => {
                    return (
                      <tr key={lead._id}>
                        {/* Name */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div 
                              style={{
                                color: '#111',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                transition: 'color 0.12s'
                              }}
                              onClick={() => handleLeadClick(lead)}
                              onMouseEnter={(e) => {
                                e.target.style.color = '#6b7280';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = '#111';
                              }}
                            >
                              {formatDisplayName(lead.name)}
                            </div>
                            {lead.hasBotChat && (
                              <span
                                title="Has chatted with the Lauft assistant"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '19px',
                                  height: '19px',
                                  borderRadius: '50%',
                                  backgroundColor: '#ece9fb',
                                  flexShrink: 0,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                }}
                              >
                                <img
                                  src={darkIcon}
                                  alt="Bot"
                                  style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                                />
                              </span>
                            )}
                            {isDemoAccount && lead.isClaimed && (
                              <span style={{
                                backgroundColor: '#f9fafb',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                color: '#6b7280',
                                padding: '2px 7px',
                                borderRadius: '5px',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                letterSpacing: '0.02em'
                              }}>
                                claimed
                              </span>
                            )}
                            {!isDemoAccount && (lead.hasDeclined || declinedLeadIds.has(lead._id)) && (
                              <span style={{
                                backgroundColor: '#f9fafb',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                color: '#6b7280',
                                padding: '2px 7px',
                                borderRadius: '5px',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                letterSpacing: '0.02em'
                              }}>
                                viewed + declined
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Tier */}
                        <td>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '1.25rem' }}>
                              {getTierEmoji(lead.leadTier)}
                            </span>
                            <span style={{
                              color: '#111',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              fontSize: '0.75rem',
                              letterSpacing: '0.04em'
                            }}>
                              {lead.leadTier || 'N/A'}
                            </span>
                          </div>
                        </td>

                        {/* Last Swiped On */}
                        <td>
                          <div style={{
                            display: 'inline-block',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            color: '#374151',
                            padding: '4px 9px',
                            borderRadius: '6px',
                            fontSize: '0.78rem',
                            fontWeight: '600'
                          }}>
                            {getLastSwipeType(lead) || 'N/A'}
                          </div>
                        </td>

                        {/* Location */}
                        <td>
                          <div style={{
                            color: '#1a1a1a',
                            fontSize: '0.9rem'
                          }}>
                            {lead.location || 'N/A'}
                          </div>
                        </td>

                        {/* Credit Score */}
                        <td>
                          <div style={{
                            color: '#1a1a1a',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            {lead.onboardingData?.creditScore || 'N/A'}
                          </div>
                        </td>

                        {/* Monthly Income */}
                        <td>
                          <div style={{
                            color: '#1a1a1a',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            {formatCurrency(calculateMonthlyIncome(
                              lead.onboardingData?.monthlyIncome,
                              lead.onboardingData?.incomeFrequency
                            ))}
                          </div>
                        </td>

                        {/* Swipes Made */}
                        <td>
                          <div
                            onClick={() => handleLeadClick(lead)}
                            style={{
                              display: 'inline-block',
                              backgroundColor: 'rgba(99, 102, 241, 0.1)',
                              border: '1px solid rgba(99, 102, 241, 0.25)',
                              color: '#4f46e5',
                              padding: '4px 9px',
                              borderRadius: '6px',
                              fontSize: '0.78rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.12s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.18)';
                              e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                              e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.25)';
                            }}
                          >
                            {(lead.likes?.length || 0) + (lead.dislikes?.length || 0)}
                          </div>
                        </td>

                        {/* Action */}
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => handleClaimClick(lead._id)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              color: '#1a1a1a',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: '700',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                              e.target.style.borderColor = '#d1d5db';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            Claim
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid rgba(0, 0, 0, 0.06)'
              }}>
                {/* Previous Button */}
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 1}
                  style={paginationButtonStyle(false, currentPage === 1)}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
                
                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span 
                      key={`ellipsis-${index}`}
                      style={{ 
                        color: '#9ca3af',
                        padding: '0 4px'
                      }}
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      style={paginationButtonStyle(currentPage === page, false)}
                      onMouseEnter={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      {page}
                    </button>
                  )
                ))}
                
                {/* Next Button */}
                <button
                  onClick={goToNext}
                  disabled={currentPage === totalPages}
                  style={paginationButtonStyle(false, currentPage === totalPages)}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280'
          }}>
            <Users size={64} style={{ 
              color: 'rgba(99, 102, 241, 0.3)',
              marginBottom: '16px'
            }} />
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1a1a1a'
            }}>
              No {activeTab === 'standing' ? 'Standing' : 'Available'} Users
            </h3>
            <p style={{ fontSize: '0.95rem' }}>
              {activeTab === 'standing' 
                ? 'There are currently no users older than 90 days in your active territories.'
                : 'There are currently no available users in your agent pools.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Claim Modal */}
      {showClaimModal && selectedLeadId && (
        <LeadClaimModal
          userId={selectedLeadId}
          onClose={() => {
            setShowClaimModal(false);
            setSelectedLeadId(null);
          }}
          onClaimed={handleClaimSuccess}
          isDemoAccount={isDemoAccount}
        />
      )}
      
      {/* Points Purchase Modal */}
      {showPointsModal && (
        <PointsPurchaseModal
          onClose={() => setShowPointsModal(false)}
        />
      )}
      
      {/* Support Chat Widget */}
      <SupportChat
        isOpen={showSupportChat}
        onClose={() => setShowSupportChat(false)}
      />
      
      {/* User Detail Modal */}
      {showLeadModal && selectedLead && (
        <AvailableLeadModal
          user={selectedLead}
          isOpen={showLeadModal}
          onClose={() => {
            setShowLeadModal(false);
            setSelectedLead(null);
          }}
          onClaim={(userId) => {
            setShowLeadModal(false);
            handleClaimClick(userId);
          }}
          onNotForMe={handleNotForMe}
          isDemoAccount={isDemoAccount}
        />
      )}
      
      {/* Active Territories Modal */}
      <ActiveTerritoriesModal
        isOpen={showTerritoriesModal}
        onClose={() => setShowTerritoriesModal(false)}
      />
    </>
  );
};

export default AvailableLeads;